/**
 * ============================================================================
 * WALLET BALANCE COMPONENT - 100% Ready
 * ============================================================================
 */

import React from 'react';
import { useWalletStore } from '../../store';
import { useI18n } from '../../providers';
import Card from '../common/Card';
import ProgressBar from '../common/ProgressBar';

/**
 * Wallet Balance Component
 * @returns {JSX.Element}
 */
const WalletBalance = () => {
  const { balance, wallet } = useWalletStore();
  const { formatCurrency, language } = useI18n();

  const available = balance?.available || 0;
  const pending = balance?.pending || 0;
  const locked = balance?.locked || 0;
  const total = available + pending + locked;

  const availablePercentage = total > 0 ? (available / total) * 100 : 0;
  const lockedPercentage = total > 0 ? (locked / total) * 100 : 0;
  const pendingPercentage = total > 0 ? (pending / total) * 100 : 0;

  return (
    <div className="space-y-6">
      {/* Main Balance Display */}
      <div className="bg-gradient-to-br from-[#22c55e] via-[#16a34a] to-[#15803d] text-white rounded-xl p-6 animate-fadeInUp shadow-lg shadow-[rgba(34,197,94,0.3)]">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center text-2xl">
              💰
            </div>
            <div>
              <h2 className="text-lg font-bold text-white/90">
                {language === 'fr' ? 'Portefeuille' : 'Wallet'}
              </h2>
              <p className="text-xs text-white/70">
                {language === 'fr' ? 'Solde disponible' : 'Available Balance'}
              </p>
            </div>
          </div>
        </div>
        <div className="space-y-2">
          <div className="text-5xl font-black text-white mb-2 animate-scaleIn">
            {formatCurrency(available, wallet?.currency || 'XAF')}
          </div>
          {total > 0 && (
            <ProgressBar
              value={availablePercentage}
              variant="success"
              showLabel={false}
              className="h-2 bg-white/20"
            />
          )}
        </div>
      </div>

      {/* Balance Breakdown */}
      <div className="bg-[#111] border border-[rgba(255,255,255,0.08)] rounded-xl p-6 animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
        <h3 className="text-lg font-black text-white mb-4">
          {language === 'fr' ? 'Détails du solde' : 'Balance Breakdown'}
        </h3>
        <div className="space-y-4">
          {/* Available */}
          <div className="flex items-center justify-between p-3 rounded-xl bg-[rgba(34,197,94,0.12)] border border-[rgba(34,197,94,0.2)]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#22c55e] to-[#16a34a] flex items-center justify-center text-xl">
                ✅
              </div>
              <div>
                <p className="text-sm font-bold text-white">
                  {language === 'fr' ? 'Disponible' : 'Available'}
                </p>
                {total > 0 && (
                  <p className="text-xs text-[#a3a3a3]">
                    {availablePercentage.toFixed(1)}% {language === 'fr' ? 'du total' : 'of total'}
                  </p>
                )}
              </div>
            </div>
            <div className="text-right">
              <p className="text-lg font-black text-[#22c55e]">
                {formatCurrency(available, wallet?.currency || 'XAF')}
              </p>
            </div>
          </div>

          {/* Pending */}
          {pending > 0 && (
            <div className="flex items-center justify-between p-3 rounded-xl bg-[rgba(234,179,8,0.12)] border border-[rgba(234,179,8,0.2)] animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center text-xl">
                  ⏳
                </div>
                <div>
                  <p className="text-sm font-bold text-white">
                    {language === 'fr' ? 'En attente' : 'Pending'}
                  </p>
                  {total > 0 && (
                    <p className="text-xs text-[#a3a3a3]">
                      {pendingPercentage.toFixed(1)}% {language === 'fr' ? 'du total' : 'of total'}
                    </p>
                  )}
                </div>
              </div>
              <div className="text-right">
                <p className="text-lg font-black text-yellow-400">
                  {formatCurrency(pending, wallet?.currency || 'XAF')}
                </p>
              </div>
            </div>
          )}

          {/* Locked */}
          {locked > 0 && (
            <div className="flex items-center justify-between p-3 rounded-xl bg-[rgba(59,130,246,0.12)] border border-[rgba(59,130,246,0.2)] animate-fadeInUp" style={{ animationDelay: '0.3s' }}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-xl">
                  🔒
                </div>
                <div>
                  <p className="text-sm font-bold text-white">
                    {language === 'fr' ? 'Verrouillé en escrow' : 'Locked in Escrow'}
                  </p>
                  {total > 0 && (
                    <p className="text-xs text-[#a3a3a3]">
                      {lockedPercentage.toFixed(1)}% {language === 'fr' ? 'du total' : 'of total'}
                    </p>
                  )}
                </div>
              </div>
              <div className="text-right">
                <p className="text-lg font-black text-blue-400">
                  {formatCurrency(locked, wallet?.currency || 'XAF')}
                </p>
              </div>
            </div>
          )}

          {/* Total */}
          {total > 0 && (
            <div className="pt-4 border-t border-[rgba(255,255,255,0.08)]">
              <div className="flex items-center justify-between">
                <p className="text-sm font-bold text-[#a3a3a3]">
                  {language === 'fr' ? 'Solde total' : 'Total Balance'}
                </p>
                <p className="text-xl font-black text-[#22c55e]">
                  {formatCurrency(total, wallet?.currency || 'XAF')}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WalletBalance;
